import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, Check, Clock, Sparkles, User, Calendar } from 'lucide-react';

/**
 * SalonBookingWizard — the shared four-step booking UI (Service → Professional → Time → Details →
 * confirmation) used by BOTH the public shareable widget (/book/:token) and the in-app customer flow.
 * The caller supplies the already-loaded menu + professionals and two callbacks (loadSlots, submit),
 * so this component is transport-agnostic (token vs authenticated). Mobile-first, salon-luxe.
 */
export interface WizardService { id: string; name: string; description: string | null; category: string | null; durationMinutes: number; price: number | null; currency: string | null }
export interface WizardStaff { id: string; displayName: string; photoUrl: string | null; specialties: string | null }
export interface WizardSubmitPayload { serviceId?: string; staffId?: string; date: string; time: string; name: string; phone?: string; notes?: string }
export interface WizardDone { time: string; date: string; serviceName: string | null; staffName: string | null }

interface Props {
  branchName: string;
  currency: string | null;
  services: WizardService[];
  staff: WizardStaff[];
  loadSlots: (staffId: string, date: string, serviceId?: string) => Promise<string[]>;
  submit: (payload: WizardSubmitPayload) => Promise<WizardDone>;
  prefillName?: string;
  prefillPhone?: string;
  /** Copy tweak: confirmation subtitle (e.g. "will confirm by phone"). */
  confirmNote?: string;
  /** Sticky action footer (full-screen widget). Set false in-app so it doesn't overlap the site footer. */
  stickyFooter?: boolean;
  /** When set, a full day offers "Join the waitlist" instead of a dead end. */
  joinWaitlist?: (payload: { serviceId?: string; staffId?: string; date: string; timePref: string; name: string; phone?: string }) => Promise<void>;
}

const todayStr = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; };
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtDur = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}` : `${m} min`);
const fmtMoney = (v: number | null, c?: string | null) => (v == null ? '' : `${c ? c + ' ' : ''}${v}`);
const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return (h || 0) * 60 + (m || 0); };
const fmtTime = (t: string) => { const [h, m] = t.split(':').map(Number); const ap = h < 12 ? 'AM' : 'PM'; const hr = h % 12 === 0 ? 12 : h % 12; return `${hr}:${String(m).padStart(2, '0')} ${ap}`; };
const prettyDate = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });

const SalonBookingWizard: React.FC<Props> = ({ branchName, currency, services, staff, loadSlots, submit, prefillName, prefillPhone, confirmNote, stickyFooter = true, joinWaitlist }) => {
  const [step, setStep] = useState(0); // 0 service · 1 professional · 2 date/time · 3 your details
  const [svc, setSvc] = useState<WizardService | null>(null);
  const [pro, setPro] = useState<WizardStaff | null>(null); // null = "Any professional"
  const [proChosen, setProChosen] = useState(false);
  const [date, setDate] = useState(todayStr());
  const [time, setTime] = useState('');
  const [slots, setSlots] = useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [name, setName] = useState(prefillName || '');
  const [phone, setPhone] = useState(prefillPhone || '');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [done, setDone] = useState<WizardDone | null>(null);
  const [waitState, setWaitState] = useState<'idle' | 'form' | 'sending' | 'done'>('idle');
  const [waitPref, setWaitPref] = useState('any');

  useEffect(() => { setName((n) => n || prefillName || ''); setPhone((p) => p || prefillPhone || ''); }, [prefillName, prefillPhone]);

  // Fetch slots on the time step (or when date / professional / service changes).
  useEffect(() => {
    if (step !== 2 || !svc) return;
    let alive = true;
    setSlotsLoading(true); setTime(''); setWaitState('idle');
    loadSlots(pro?.id || '', date, svc.id)
      .then((s) => { if (alive) setSlots(s || []); })
      .catch(() => { if (alive) setSlots([]); })
      .finally(() => { if (alive) setSlotsLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, date, pro, svc]);

  const categories = useMemo(() => {
    const groups = new Map<string, WizardService[]>();
    for (const s of services) { const k = s.category || 'Services'; if (!groups.has(k)) groups.set(k, []); groups.get(k)!.push(s); }
    return Array.from(groups.entries());
  }, [services]);

  const dateStrip = useMemo(() => Array.from({ length: 14 }, (_, i) => { const d = new Date(); d.setDate(d.getDate() + i); return d; }), []);
  const slotGroups = useMemo(() => {
    const g: Record<string, string[]> = { Morning: [], Afternoon: [], Evening: [] };
    for (const t of slots) { const m = toMin(t); if (m < 12 * 60) g.Morning.push(t); else if (m < 17 * 60) g.Afternoon.push(t); else g.Evening.push(t); }
    return Object.entries(g).filter(([, v]) => v.length);
  }, [slots]);

  // Phone is optional, but if entered it must be a real number (we confirm bookings on it).
  const phoneValid = (() => {
    const p = phone.trim();
    if (!p) return true;
    const digits = p.replace(/\D/g, '');
    return /^\+?[\d\s()-]+$/.test(p) && digits.length >= 7 && digits.length <= 15;
  })();
  const canContinue = step === 0 ? !!svc : step === 1 ? proChosen : step === 2 ? !!time : (!!name.trim() && phoneValid);
  const back = () => { if (step > 0) setStep((s) => s - 1); };

  const doSubmit = async () => {
    if (!svc || submitting) return;
    setSubmitting(true); setMessage('');
    try {
      const d = await submit({ serviceId: svc.id, staffId: pro?.id || undefined, date, time, name: name.trim(), phone: phone.trim() || undefined, notes: notes.trim() || undefined });
      setDone({ time: d.time || time, date: d.date || date, serviceName: d.serviceName || svc.name, staffName: d.staffName || pro?.displayName || null });
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Could not request your booking. Please try again.');
      if (String(e).toLowerCase().includes('just taken')) setStep(2);
    } finally { setSubmitting(false); }
  };
  const submitWait = async () => {
    if (!joinWaitlist || !name.trim() || waitState === 'sending') return;
    setWaitState('sending'); setMessage('');
    try { await joinWaitlist({ serviceId: svc?.id, staffId: pro?.id, date, timePref: waitPref, name: name.trim(), phone: phone.trim() || undefined }); setWaitState('done'); }
    catch (e) { setMessage(e instanceof Error ? e.message : 'Could not join the waitlist.'); setWaitState('form'); }
  };
  const primary = () => (step < 3 ? setStep((s) => s + 1) : doSubmit());
  const reset = () => { setStep(0); setSvc(null); setPro(null); setProChosen(false); setTime(''); setNotes(''); setDone(null); setMessage(''); };

  const STEP_LABELS = ['Service', 'Professional', 'Time', 'Details'];

  if (done) {
    return (
      <div style={st.doneWrap}>
        <div style={st.doneBadge}><Check size={40} color="#fff" strokeWidth={3} /></div>
        <div style={st.doneTitle}>Booking requested</div>
        <div style={st.muted}>{branchName} will confirm your appointment shortly{confirmNote ? ` ${confirmNote}` : phone ? ' by phone' : ''}.</div>
        <div style={st.summaryCard}>
          <SummaryRow icon={<Sparkles size={16} />} label={done.serviceName || 'Service'} sub={svc ? `${fmtDur(svc.durationMinutes)}${svc.price != null ? ` · ${fmtMoney(svc.price, currency)}` : ''}` : ''} />
          <SummaryRow icon={<User size={16} />} label={done.staffName || 'Any professional'} />
          <SummaryRow icon={<Calendar size={16} />} label={prettyDate(done.date)} sub={fmtTime(done.time)} />
        </div>
        <button style={st.primaryBtn} onClick={reset}>Book another</button>
      </div>
    );
  }

  return (
    <div style={st.shell}>
      <div style={st.header}>
        {step > 0 ? <button style={st.backBtn} onClick={back} aria-label="Back"><ChevronLeft size={22} /></button> : <div style={st.brandMark}><Sparkles size={18} color="#fff" /></div>}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={st.brandName}>{branchName}</div>
          <div style={st.stepHint}>{STEP_LABELS[step]} · Step {step + 1} of 4</div>
        </div>
      </div>
      <div style={st.progress}>{STEP_LABELS.map((_, i) => <div key={i} style={{ ...st.progressSeg, background: i <= step ? 'var(--cu-accent)' : 'rgba(33,27,24,0.10)' }} />)}</div>

      <div style={st.body}>
        {message && <div style={st.errorBox}>{message}</div>}

        {step === 0 && (
          <div>
            <h1 style={st.h1}>What can we do for you?</h1>
            {services.length === 0 ? <div style={st.muted}>No services are listed yet — please contact the salon.</div> : categories.map(([cat, list]) => (
              <div key={cat} style={{ marginBottom: 18 }}>
                <div style={st.catLabel}>{cat}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {list.map((s) => {
                    const on = svc?.id === s.id;
                    return (
                      <button key={s.id} type="button" style={{ ...st.serviceCard, ...(on ? st.serviceCardOn : {}) }} onClick={() => { setSvc(s); setTime(''); }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={st.serviceName}>{s.name}</div>
                          <div style={st.serviceMeta}><Clock size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />{fmtDur(s.durationMinutes)}</div>
                          {s.description && <div style={st.serviceDesc}>{s.description}</div>}
                        </div>
                        <div style={st.serviceRight}>
                          {s.price != null && <div style={st.servicePrice}>{fmtMoney(s.price, currency)}</div>}
                          <span style={{ ...st.radio, ...(on ? st.radioOn : {}) }}>{on && <Check size={13} color="#fff" strokeWidth={3} />}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {step === 1 && (
          <div>
            <h1 style={st.h1}>Choose your professional</h1>
            <div style={st.proGrid}>
              <button type="button" style={{ ...st.proCard, ...(proChosen && !pro ? st.proCardOn : {}) }} onClick={() => { setPro(null); setProChosen(true); }}>
                <div style={{ ...st.proAvatar, background: 'linear-gradient(135deg,#c79a5b,#a56a3c)' }}><Sparkles size={22} color="#fff" /></div>
                <div style={st.proName}>Any professional</div>
                <div style={st.proSpec}>Best availability</div>
              </button>
              {staff.map((p) => {
                const on = proChosen && pro?.id === p.id;
                return (
                  <button key={p.id} type="button" style={{ ...st.proCard, ...(on ? st.proCardOn : {}) }} onClick={() => { setPro(p); setProChosen(true); }}>
                    <div style={st.proAvatar}>{p.photoUrl ? <img src={p.photoUrl} alt={p.displayName} style={st.proImg} /> : <span style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{p.displayName.trim().charAt(0).toUpperCase()}</span>}</div>
                    <div style={st.proName}>{p.displayName}</div>
                    {p.specialties && <div style={st.proSpec}>{p.specialties}</div>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h1 style={st.h1}>Pick a time</h1>
            <div style={st.dateStrip}>
              {dateStrip.map((d) => {
                const v = iso(d); const on = date === v;
                return (
                  <button key={v} type="button" style={{ ...st.datePill, ...(on ? st.datePillOn : {}) }} onClick={() => setDate(v)}>
                    <span style={st.dateDow}>{d.toLocaleDateString(undefined, { weekday: 'short' })}</span>
                    <span style={st.dateNum}>{d.getDate()}</span>
                    <span style={st.dateMon}>{d.toLocaleDateString(undefined, { month: 'short' })}</span>
                  </button>
                );
              })}
            </div>
            {slotsLoading ? <div style={st.muted}>Finding available times…</div>
              : slotGroups.length === 0 ? (
                <div>
                  <div style={st.emptySlots}>No free times on {prettyDate(date)}.<br />Try another day above{joinWaitlist ? ', or join the waitlist.' : '.'}</div>
                  {joinWaitlist && waitState === 'done' ? (
                    <div style={st.waitOk}>✓ You’re on the waitlist. {branchName} will contact you if a slot opens on this day.</div>
                  ) : joinWaitlist && (waitState === 'form' || waitState === 'sending') ? (
                    <div style={st.waitCard}>
                      <div style={st.catLabel}>Join the waitlist</div>
                      <input style={st.input} value={name} maxLength={100} placeholder="Your name" onChange={(e) => setName(e.target.value)} />
                      <input style={{ ...st.input, ...(phoneValid ? {} : { borderColor: '#dc2626' }) }} value={phone} maxLength={20} inputMode="tel" placeholder="Phone, e.g. +9715XXXXXXXX" onChange={(e) => setPhone(e.target.value)} />
                      {!phoneValid && <span style={st.phoneHint}>Enter a valid phone number, or leave it blank.</span>}
                      <select style={st.input} value={waitPref} onChange={(e) => setWaitPref(e.target.value)}>
                        <option value="any">Any time</option>
                        <option value="morning">Morning</option>
                        <option value="afternoon">Afternoon</option>
                        <option value="evening">Evening</option>
                      </select>
                      <button style={{ ...st.primaryBtn, opacity: name.trim() && phoneValid && waitState !== 'sending' ? 1 : 0.5 }} disabled={!name.trim() || !phoneValid || waitState === 'sending'} onClick={submitWait}>{waitState === 'sending' ? 'Joining…' : 'Join the waitlist'}</button>
                    </div>
                  ) : joinWaitlist ? (
                    <button style={st.waitBtn} onClick={() => setWaitState('form')}>⏳ Join the waitlist for this day</button>
                  ) : null}
                </div>
              )
              : slotGroups.map(([grp, list]) => (
                <div key={grp} style={{ marginBottom: 14 }}>
                  <div style={st.catLabel}>{grp}</div>
                  <div style={st.slotGrid}>{list.map((t) => <button key={t} type="button" style={{ ...st.slot, ...(time === t ? st.slotOn : {}) }} onClick={() => setTime(t)}>{fmtTime(t)}</button>)}</div>
                </div>
              ))}
          </div>
        )}

        {step === 3 && (
          <div>
            <h1 style={st.h1}>Almost there</h1>
            <div style={st.reviewCard}>
              <SummaryRow icon={<Sparkles size={16} />} label={svc?.name || 'Service'} sub={svc ? `${fmtDur(svc.durationMinutes)}${svc.price != null ? ` · ${fmtMoney(svc.price, currency)}` : ''}` : ''} />
              <SummaryRow icon={<User size={16} />} label={pro?.displayName || 'Any professional'} />
              <SummaryRow icon={<Calendar size={16} />} label={prettyDate(date)} sub={time ? fmtTime(time) : ''} />
            </div>
            <label style={st.fieldLabel}>Your name *
              <input style={st.input} value={name} maxLength={100} placeholder="e.g. Priya Sharma" onChange={(e) => setName(e.target.value)} />
            </label>
            <label style={st.fieldLabel}>Phone <span style={st.optional}>(so we can confirm)</span>
              <input style={{ ...st.input, ...(phoneValid ? {} : { borderColor: '#dc2626' }) }} value={phone} maxLength={20} inputMode="tel" placeholder="Mobile number, e.g. +9715XXXXXXXX" onChange={(e) => setPhone(e.target.value)} />
              {!phoneValid && <span style={st.phoneHint}>Enter a valid phone number, or leave it blank.</span>}
            </label>
            <label style={st.fieldLabel}>Anything we should know? <span style={st.optional}>(optional)</span>
              <textarea style={{ ...st.input, minHeight: 64, resize: 'vertical' }} value={notes} maxLength={500} placeholder="Requests, preferences…" onChange={(e) => setNotes(e.target.value)} />
            </label>
          </div>
        )}
      </div>

      <div style={stickyFooter ? st.footer : st.footerStatic}>
        {svc && (
          <div style={st.footSummary}>
            <span style={st.footSvc}>{svc.name}</span>
            <span style={st.footMeta}>{fmtDur(svc.durationMinutes)}{time ? ` · ${prettyDate(date)}, ${fmtTime(time)}` : ''}{svc.price != null ? ` · ${fmtMoney(svc.price, currency)}` : ''}</span>
          </div>
        )}
        <button style={{ ...st.primaryBtn, opacity: canContinue && !submitting ? 1 : 0.5 }} disabled={!canContinue || submitting} onClick={primary}>
          {submitting ? 'Requesting…' : step < 3 ? 'Continue' : 'Request booking'}
        </button>
      </div>
      <style>{`@keyframes cuBookPop{0%{transform:scale(0.6);opacity:0}60%{transform:scale(1.08)}100%{transform:scale(1);opacity:1}}`}</style>
    </div>
  );
};

const SummaryRow: React.FC<{ icon: React.ReactNode; label: string; sub?: string }> = ({ icon, label, sub }) => (
  <div style={st.sumRow}>
    <span style={st.sumIcon}>{icon}</span>
    <span style={{ flex: 1, minWidth: 0 }}><span style={st.sumLabel}>{label}</span>{sub ? <span style={st.sumSub}>{sub}</span> : null}</span>
  </div>
);

const SERIF = "'Fraunces','Iowan Old Style','Palatino Linotype','Palatino','Georgia',serif";
const SANS = "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif";

const st: Record<string, React.CSSProperties> = {
  shell: { display: 'flex', flexDirection: 'column', minHeight: '100%', fontFamily: SANS, color: '#211b18', ['--cu-accent' as any]: '#a56a3c' },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0 12px' },
  backBtn: { width: 40, height: 40, borderRadius: 12, border: '1px solid #e3d8c8', background: '#fff', color: '#211b18', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 },
  brandMark: { width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#a56a3c,#c79a5b)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  brandName: { fontFamily: SERIF, fontSize: '1.15rem', fontWeight: 600, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  stepHint: { fontSize: '0.74rem', color: '#a08a75', letterSpacing: 0.3, textTransform: 'uppercase', fontWeight: 600, marginTop: 2 },
  progress: { display: 'flex', gap: 5, padding: '0 0 12px' },
  progressSeg: { flex: 1, height: 4, borderRadius: 999, transition: 'background 0.3s' },

  body: { flex: 1, padding: '6px 0 20px' },
  h1: { fontFamily: SERIF, fontSize: '1.55rem', fontWeight: 600, lineHeight: 1.2, margin: '4px 0 18px', textWrap: 'balance' as any },
  catLabel: { fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.6, color: '#a08a75', textTransform: 'uppercase', margin: '0 2px 8px' },
  muted: { color: '#8a7d6f', fontSize: '0.95rem', lineHeight: 1.5 },
  errorBox: { background: '#fbeaea', color: '#b91c1c', borderRadius: 12, padding: '10px 14px', fontSize: '0.88rem', marginBottom: 14, border: '1px solid #f3cfcf' },

  serviceCard: { display: 'flex', alignItems: 'flex-start', gap: 12, background: '#fff', border: '1.5px solid #ece2d3', borderRadius: 16, padding: '14px 16px', cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'border-color 0.15s, box-shadow 0.15s' },
  serviceCardOn: { borderColor: 'var(--cu-accent)', boxShadow: '0 4px 18px rgba(165,106,60,0.16)' },
  serviceName: { fontSize: '1rem', fontWeight: 700, color: '#211b18' },
  serviceMeta: { fontSize: '0.82rem', color: '#a08a75', marginTop: 3 },
  serviceDesc: { fontSize: '0.82rem', color: '#8a7d6f', marginTop: 6, lineHeight: 1.4 },
  serviceRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, flexShrink: 0 },
  servicePrice: { fontFamily: SERIF, fontSize: '1.02rem', fontWeight: 600, color: '#211b18' },
  radio: { width: 24, height: 24, borderRadius: '50%', border: '2px solid #d9cbb7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  radioOn: { background: 'var(--cu-accent)', borderColor: 'var(--cu-accent)' },

  proGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 },
  proCard: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, background: '#fff', border: '1.5px solid #ece2d3', borderRadius: 18, padding: '18px 12px', cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s' },
  proCardOn: { borderColor: 'var(--cu-accent)', boxShadow: '0 4px 18px rgba(165,106,60,0.16)' },
  proAvatar: { width: 66, height: 66, borderRadius: '50%', background: 'linear-gradient(135deg,#8a5a3b,#c79a5b)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: 2 },
  proImg: { width: '100%', height: '100%', objectFit: 'cover' },
  proName: { fontSize: '0.95rem', fontWeight: 700, textAlign: 'center', color: '#211b18' },
  proSpec: { fontSize: '0.76rem', color: '#a08a75', textAlign: 'center', lineHeight: 1.3 },

  dateStrip: { display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, marginBottom: 16, scrollbarWidth: 'none' as any },
  datePill: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, minWidth: 58, background: '#fff', border: '1.5px solid #ece2d3', borderRadius: 14, padding: '10px 4px', cursor: 'pointer', flexShrink: 0 },
  datePillOn: { background: 'var(--cu-accent)', borderColor: 'var(--cu-accent)', color: '#fff' },
  dateDow: { fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', opacity: 0.7 },
  dateNum: { fontFamily: SERIF, fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.1 },
  dateMon: { fontSize: '0.66rem', opacity: 0.7, textTransform: 'uppercase' },
  slotGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 },
  slot: { border: '1.5px solid #ece2d3', background: '#fff', color: '#211b18', borderRadius: 12, padding: '11px 4px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' },
  slotOn: { background: 'var(--cu-accent)', color: '#fff', borderColor: 'var(--cu-accent)' },
  emptySlots: { color: '#8a7d6f', fontSize: '0.95rem', textAlign: 'center', padding: '28px 12px 14px', lineHeight: 1.6 },
  waitBtn: { display: 'block', width: '100%', background: '#fff', color: 'var(--cu-accent)', border: '1.5px solid var(--cu-accent)', borderRadius: 14, padding: '13px', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' },
  waitCard: { display: 'flex', flexDirection: 'column', gap: 10, background: '#fff', border: '1.5px solid #ece2d3', borderRadius: 16, padding: 16 },
  waitOk: { background: 'rgba(63,122,46,0.10)', color: '#3f7a2e', border: '1px solid rgba(63,122,46,0.3)', borderRadius: 12, padding: '12px 14px', fontSize: '0.9rem', textAlign: 'center', lineHeight: 1.5 },

  reviewCard: { background: '#fff', border: '1.5px solid #ece2d3', borderRadius: 16, padding: '6px 16px', marginBottom: 18 },
  fieldLabel: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.82rem', fontWeight: 700, color: '#6b5d4f', marginBottom: 14 },
  optional: { fontWeight: 500, color: '#a08a75' },
  phoneHint: { fontWeight: 500, color: '#dc2626', fontSize: '0.78rem' },
  input: { border: '1.5px solid #e3d8c8', borderRadius: 12, padding: '12px 14px', fontSize: '0.95rem', color: '#211b18', background: '#fff', outline: 'none', width: '100%', boxSizing: 'border-box', fontWeight: 500, fontFamily: SANS },

  sumRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f0e8db' },
  sumIcon: { width: 34, height: 34, borderRadius: 10, background: 'rgba(165,106,60,0.10)', color: 'var(--cu-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  sumLabel: { display: 'block', fontWeight: 700, color: '#211b18', fontSize: '0.96rem' },
  sumSub: { display: 'block', fontSize: '0.82rem', color: '#a08a75', marginTop: 2 },

  footer: { position: 'sticky', bottom: 0, padding: '12px 0 calc(14px + env(safe-area-inset-bottom))', borderTop: '1px solid #ece2d3', background: 'rgba(246,241,234,0.94)', backdropFilter: 'blur(8px)', display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 },
  footerStatic: { padding: '14px 0 20px', borderTop: '1px solid #ece2d3', display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 },
  footSummary: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 },
  footSvc: { fontWeight: 700, fontSize: '0.92rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  footMeta: { fontSize: '0.78rem', color: '#a08a75' },
  primaryBtn: { background: 'linear-gradient(135deg,#a56a3c,#8a5a3b)', color: '#fff', border: 'none', borderRadius: 14, padding: '15px', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', boxShadow: '0 10px 26px rgba(165,106,60,0.32)', width: '100%' },

  doneWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '48px 24px', gap: 12 },
  doneBadge: { width: 84, height: 84, borderRadius: '50%', background: 'linear-gradient(135deg,#5a8a3b,#3f7a2e)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 14px 34px rgba(63,122,46,0.34)', animation: 'cuBookPop 0.5s ease-out' },
  doneTitle: { fontFamily: SERIF, fontSize: '1.7rem', fontWeight: 600, marginTop: 6 },
  summaryCard: { background: '#fff', border: '1.5px solid #ece2d3', borderRadius: 16, padding: '6px 18px', width: '100%', maxWidth: 360, margin: '10px 0 6px', textAlign: 'left' },
};

/** Loads the elegant display face once; falls back silently to the serif stack if the CDN is blocked. */
export const useSalonFont = () => {
  useEffect(() => {
    const id = 'cu-book-font';
    if (document.getElementById(id)) return;
    const l = document.createElement('link');
    l.id = id; l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&display=swap';
    document.head.appendChild(l);
  }, []);
};

export default SalonBookingWizard;
